import React from 'react';
import {
  Platform,
  Modal as ReactNativeModal,
  ScrollView,
  StyleProp,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import { BlurView } from '@components/BlurView';
import { Button } from '@components/Button';
import { Icon } from '@components/Icon';
import { StyleObj, makeStyles } from '@providers/Theme';

export type ModalProps = {
  open: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  dismissOnBackdropPress?: boolean;
  animationType?: 'none' | 'slide' | 'fade';
  showBackButton?: boolean;
  showCloseButton?: boolean;
  onBackButtonPress?: () => void;
  onDismiss?: () => void;
  paperStyle?: StyleProp<StyleObj>;
};

const useStyles = makeStyles((theme) => ({
  blurView: {
    width: '100%',
    height: '100%',
    backgroundColor:
      Platform.OS === 'android' ? 'rgba(52, 64, 84, 0.5)' : undefined,
  },
  backdrop: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  paper: {
    backgroundColor: 'white',
    width: '90%',
    maxWidth: 424,
    borderRadius: 12,
    ...theme.mixins.padding(2),
    display: 'flex',
    flexDirection: 'column',
    // CRITICAL: Force the container to honor limits and allow the ScrollView to shrink
    overflow: 'hidden', 
    on: {
      lg: { maxHeight: '90%' },
      md: { maxHeight: '60%' },
      sm: { maxHeight: '60%' },
      xs: { maxHeight: '90%' },
    },
  },
  scrollView: {
    flexGrow: 1, // Let scroll fill container
  },
  scrollContent: {
    flexGrow: 1, // Ensures content lays out correctly inside scroll view
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  content: {},
}));

export const Modal: React.FC<ModalProps> = ({
  open,
  onRequestClose,
  children,
  dismissOnBackdropPress = true,
  animationType = 'fade',
  showBackButton = false,
  showCloseButton = true,
  onBackButtonPress,
  onDismiss,
  paperStyle,
}) => {
  const styles = useStyles();

  return (
    <ReactNativeModal
      visible={open}
      transparent
      onRequestClose={onRequestClose}
      animationType={animationType}
      onDismiss={onDismiss}>
      <BlurView style={styles.blurView} blurType="dark" blurAmount={2}>
        <TouchableWithoutFeedback
          onPress={dismissOnBackdropPress ? onRequestClose : undefined}>
          <View style={styles.backdrop}>
            {/* 
              FIX: Changed inner touchable to a View with pointerEvents on Web 
              or stopPropagation handler to avoid breaking web scroll mechanics.
            */}
            <View 
              style={[styles.paper, paperStyle]} 
              onTouchStart={(e) => e.stopPropagation()} // Prevents backdrop press triggers
            >
              <ScrollView 
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled" // Fixes touch responsiveness on Android
              >
                <View style={styles.header}>
                  {showBackButton && (
                    <Button
                      testID="modal-back-button"
                      onPress={onBackButtonPress}
                      startIcon={
                        <Icon
                          name="arrow-left"
                          size={24}
                          color={(theme) => theme.palette.cool[700]}
                        />
                      }
                    />
                  )}
                  <View testID="gap" />
                  {showCloseButton && (
                    <Button
                      onPress={onRequestClose}
                      startIcon={
                        <Icon
                          name="close"
                          size={24}
                          color={(theme) => theme.palette.cool[700]}
                        />
                      }
                    />
                  )}
                </View>
                <View style={styles.content}>{children}</View>
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </BlurView>
    </ReactNativeModal>
  );
};
